import type React from "react";
import { useEffect, useCallback, useMemo, useRef, useState } from "react";

import { AddOnType, historyHookPreselect } from "app/contexts/AddonStateContext/const";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { useTotalVisitsPeakQuery } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TotalVisitsComponentHistoryHook/useTotalVisitsPeakQuery";
import { allTrackers } from "app/services/track/track";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";

const trackEventName = "total visit hook";

export type MajorPeakType = "yoy" | "trend" | "season" | null;

interface IUseTotalVisitsComponentHistoryHookApi {
    ctaRef: React.RefObject<HTMLDivElement>;
    isHistoryHookDisplayed: boolean;
    isPulsarShown: boolean;
    majorPeakType: MajorPeakType;
    onMouseEnter: () => void;
    onHookClick: () => void;
    onCloseCta: () => void;
    onOpenCta: () => void;
    onMouseLeave: () => void;
}

export const useTotalVisitsComponentHistoryHook = (): IUseTotalVisitsComponentHistoryHookApi => {
    const hasTotalVisitsHook = GrowthbookService.growthbookInstance.isOn(TestsNames.TOTAL_VISITS_HOOK);
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const clickOutsideRef = useRef(null);
    const ctaRef = useRef<HTMLDivElement>(null);

    const [isOpenable, setIsOpenable] = useState(true);
    const [isHistoryHookDisplayed, setIsHistoryHookDisplayed] = useState(false);

    const { isLoading, data: peaksData } = useTotalVisitsPeakQuery({ hasTotalVisitsHook });

    const { getAddon } = useAddonsState();

    const majorPeakType = useMemo(() => {
        if (isLoading) {
            return null;
        }

        if (peaksData?.trendReversedKeys.length > 0) {
            return "trend";
        }

        if (peaksData?.seasonalPeaksKeys.length > 0) {
            return "season";
        }

        if (peaksData?.yearOverYearChangeOverThresholdKeys.length > 0) {
            return "yoy";
        }

        return null;
    }, [isLoading, peaksData]);

    const isPulsarShown = !getAddon(AddOnType.HistoricalData) && !!majorPeakType;

    const onCloseCta = useMemo(() => {
        if (!isHistoryHookDisplayed) {
            return null;
        }

        return () => {
            setIsHistoryHookDisplayed(false);
            allTrackers.dispatchTrackEvent("button", "close", `${trackEventName}/${majorPeakType}`);
        };
    }, [majorPeakType, isHistoryHookDisplayed]);

    const onOpenCta = useCallback(() => {
        setIsOpenable(false);
        setIsHistoryHookDisplayed(true);
        allTrackers.dispatchTrackEvent("button", "click", `${trackEventName}/${majorPeakType}`);
    }, [majorPeakType]);

    const onMouseEnter = useMemo(() => {
        if (!majorPeakType || !hasTotalVisitsHook || !isOpenable || !isPulsarShown) {
            return null;
        }

        return () => {
            if (!isHistoryHookDisplayed) {
                onOpenCta();
            }
        };
    }, [majorPeakType, hasTotalVisitsHook, isOpenable, isPulsarShown, isHistoryHookDisplayed, onOpenCta]);

    const onMouseLeave = useMemo(() => {
        if (!hasTotalVisitsHook) {
            return null;
        }

        return () => {
            setIsOpenable(true);

            if (isHistoryHookDisplayed) {
                onCloseCta();
            }
        };
    }, [isHistoryHookDisplayed, hasTotalVisitsHook, onCloseCta]);

    const onHookClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("internal link", "click", `${trackEventName}/${majorPeakType}`);
        handlePurchaseAddonClick(historyHookPreselect);
    }, [handlePurchaseAddonClick, majorPeakType]);

    useEffect(() => {
        if (isHistoryHookDisplayed) {
            setTimeout(() => {
                document.addEventListener("click", clickOutsideRef.current);
            });
            return;
        }

        document.removeEventListener("click", clickOutsideRef.current);
    }, [isHistoryHookDisplayed]);

    useEffect(() => {
        clickOutsideRef.current = (event: React.PointerEvent) => {
            if (!ctaRef.current.contains(event.target as HTMLElement)) {
                setIsHistoryHookDisplayed(false);
            }
        };

        return () => {
            document.removeEventListener("click", clickOutsideRef.current);
        };
    }, []);

    return {
        ctaRef,
        isHistoryHookDisplayed,
        isPulsarShown,
        majorPeakType,
        onMouseEnter,
        onHookClick,
        onCloseCta,
        onOpenCta,
        onMouseLeave,
    };
};
